import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Container, Typography, Card, CardContent, Stack, Box, Button } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../components/toast';
import { getUserGroups } from '../../utils';
import { LoadingWrapper } from '../../components/loading';
import { useLoadingState } from '../../hooks/useLoadingState';
import { HRPageHeader } from '../../components/hr';

// Mock data for HR dashboard
const mockHRStats = {
  totalEmployees: 45,
  activeEmployees: 42,
  pendingLeaveRequests: 8,
  totalPayroll: 125000,
  pendingExpenses: 2340,
  averageAttendance: 94.5,
};

const hrModules = [
  {
    title: 'Employee Management',
    description: 'Manage employee profiles, onboarding, and offboarding processes',
    icon: <PeopleAltIcon sx={{ fontSize: 40 }} />,
    path: '/hr/employees',
    color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    stats: `${mockHRStats.totalEmployees} Employees`,
  },
  {
    title: 'Attendance Tracking',
    description: 'Monitor employee attendance, time tracking, and work schedules',
    icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
    path: '/hr/attendance',
    color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    stats: `${mockHRStats.averageAttendance}% Attendance`,
  },
  {
    title: 'Leave Management',
    description: 'Handle leave requests, approvals, and leave policy management',
    icon: <BeachAccessIcon sx={{ fontSize: 40 }} />,
    path: '/hr/leave',
    color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    stats: `${mockHRStats.pendingLeaveRequests} Pending`,
  },
  {
    title: 'Payroll Processing',
    description: 'Process payroll, manage salaries, and handle compensation',
    icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
    path: '/hr/payroll',
    color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    stats: `$${mockHRStats.totalPayroll.toLocaleString()}`,
  },
  {
    title: 'Expense Management',
    description: 'Track employee expenses, approvals, and reimbursements',
    icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
    path: '/hr/expenses',
    color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    stats: `$${mockHRStats.pendingExpenses} Pending`,
  },
  {
    title: 'Inventory Management',
    description: 'Manage inventory items, stock levels, and equipment tracking',
    icon: <InventoryIcon sx={{ fontSize: 40 }} />,
    path: '/hr/inventory',
    color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    stats: 'Manage Items',
  },
  {
    title: 'Inventory Assignments',
    description: 'Assign inventory items to employees and track their usage',
    icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
    path: '/hr/inventory-assignments',
    color: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    stats: 'Track Assignments',
  },
  {
    title: 'Reports & Analytics',
    description: 'Generate comprehensive HR reports and analytics',
    icon: <BarChartIcon sx={{ fontSize: 40 }} />,
    path: '/hr/reports',
    color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    stats: 'View Reports',
  },
  {
    title: 'Settings & Configuration',
    description: 'Configure HR policies, system settings, and preferences',
    icon: <SettingsIcon sx={{ fontSize: 40 }} />,
    path: '/hr/settings',
    color: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    stats: 'Configure',
  },
];

const allowedGroups = ['admin', 'hr'];


export default function HRDashboardPage() {
  const navigate = useNavigate();
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some(group => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }

    // Load HR dashboard data
    withLoading(async () => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return mockHRStats;
    }).catch(() => {
      toast.error('Cannot load HR dashboard data, try again later.');
    });
  }, [navigate, withLoading]);

  const handleModuleClick = (path) => {
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | HR Dashboard</title>
      </Helmet>
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HRPageHeader
              title="HR dashboard"
              description="Your central hub for workforce operations. Review key metrics at a glance and open any module to manage employees, attendance, payroll, and more."
            />
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12}>
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Total Employees
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <PeopleAltIcon />
                      <Typography variant="h4">{mockHRStats.totalEmployees}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Active Employees
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <PeopleAltIcon />
                      <Typography variant="h4">{mockHRStats.activeEmployees}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Pending Leave Requests
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <BeachAccessIcon />
                      <Typography variant="h4">{mockHRStats.pendingLeaveRequests}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Average Attendance
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <AccessTimeIcon />
                      <Typography variant="h4">{mockHRStats.averageAttendance}%</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* HR Modules */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              HR Management Modules
            </Typography>
            <LoadingWrapper isLoading={isLoading} skeletonCount={7}>
              <Grid container spacing={3}>
                {hrModules.map((module) => (
                  <Grid item xs={12} sm={6} md={4} key={module.title}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: (theme) => `0 12px 40px -12px ${theme.palette.primary.main}40`,
                          '& .module-icon': {
                            transform: 'scale(1.1)',
                          },
                          '& .arrow-icon': {
                            opacity: 1,
                            transform: 'translateX(0)',
                          },
                        },
                      }}
                      onClick={() => handleModuleClick(module.path)}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box
                            className="module-icon"
                            sx={{
                              transition: 'transform 0.3s ease',
                              display: 'flex',
                              justifyContent: 'center',
                              color: 'white',
                              p: 2,
                              borderRadius: 2,
                              background: module.color,
                            }}
                          >
                            {module.icon}
                          </Box>
                          <Typography variant="h6" gutterBottom>
                            {module.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {module.description}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" color="primary">
                              {module.stats}
                            </Typography>
                            <ArrowForwardIcon
                              className="arrow-icon"
                              sx={{
                                opacity: 0,
                                transform: 'translateX(-10px)',
                                transition: 'all 0.3s ease',
                                color: 'primary.main',
                              }}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </LoadingWrapper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    startIcon={<PeopleAltIcon />}
                    onClick={() => handleModuleClick('/hr/employees')}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      },
                    }}
                  >
                    Add New Employee
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<BeachAccessIcon />}
                    onClick={() => handleModuleClick('/hr/leave')}
                    sx={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                      },
                    }}
                  >
                    Review Leave Requests
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AttachMoneyIcon />}
                    onClick={() => handleModuleClick('/hr/payroll')}
                    sx={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
                      },
                    }}
                  >
                    Process Payroll
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ReceiptIcon />}
                    onClick={() => handleModuleClick('/hr/expenses')}
                    sx={{
                      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #fee140 0%, #fa709a 100%)',
                      },
                    }}
                  >
                    Review Expenses
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<BarChartIcon />}
                    onClick={() => handleModuleClick('/hr/reports')}
                    sx={{
                      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                      color: 'text.primary',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #fed6e3 0%, #a8edea 100%)',
                      },
                    }}
                  >
                    Generate Reports
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
